import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { toDatetimeLocal } from '../../../utils/datetime';
import FieldError from '../../../components/atoms/forms/FieldError';

// Per-route vehicle assignment header. Renders nothing when `vehicle` is
// absent (BE hasn't delivered fleet data yet, or the route has no
// assignment). When present, surfaces an AM/PM + skill pill derived from
// shift_start_time + skillSet, and three editable datetime fields (shift
// start, target shift end, shift end). Edits flow up via the three
// onChange callbacks; this component owns no state. Layout does not wrap —
// when its combined width exceeds the route card it scrolls horizontally.
// Driver name and vehicle id are not
// rendered — those fields are not yet available from the backend.

// Pure derivation of AM/PM from a shift-start timestamp. Accepts ISO
// ("YYYY-MM-DDTHH:MM:SS") and space-separated ("YYYY-MM-DD HH:MM:SS")
// forms — BE has used both in fixtures.
const ISO_HOUR_RE = /T(\d{2})/;
const SPACE_HOUR_RE = / (\d{2}):/;

export function deriveShiftPeriod(shiftStartTime) {
  if (!shiftStartTime) return '';
  const isoMatch = ISO_HOUR_RE.exec(shiftStartTime);
  if (isoMatch) {
    const hour = Number.parseInt(isoMatch[1], 10);
    return hour < 12 ? 'AM' : 'PM';
  }
  const spaceMatch = SPACE_HOUR_RE.exec(shiftStartTime);
  if (spaceMatch) {
    const hour = Number.parseInt(spaceMatch[1], 10);
    return hour < 12 ? 'AM' : 'PM';
  }
  return '';
}

// Display helper for read-only shift times. Converts datetime-local format
// to readable local text (e.g., "2026-06-19 06:00" instead of "2026-06-19T06:00").
function staticTimeText(value) {
  const local = toDatetimeLocal(value);
  return local ? local.replace('T', ' ') : '—';
}

// Always renders as its own row below the route title — checkbox + route
// name + totes + expand stay on row 1, this wrapper claims a full row
// (`flex-basis: 100%`, `order: 2`) and overflows horizontally when the
// contents (AM pill + 3 datetime inputs) don't
// fit. Mouse-wheel scrolls horizontally via the onWheel handler below.
const StyledDiv = styledComponent('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: theme.spacing.fieldGap,
  flexBasis: '100%',
  order: 2,
  overflowX: 'auto',
  paddingTop: theme.spacing.labelGap,
  borderTop: `1px solid ${theme.colors.borderLight}`,

  // Every direct flex item in the scroll row keeps its natural width:
  // no shrinking, no wrapping. When the combined width exceeds the
  // route card, the wrapper scrolls horizontally instead of squeezing
  // any of the children.
  '& > *': {
    flexShrink: 0,
  },
  '& .rvh-pill': {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing.labelGap} ${theme.spacing.gapMd}`,
    borderRadius: theme.radii.pill,
    background: theme.colors.warningBg,
    color: theme.colors.warningText,
    minWidth: '52px',
    lineHeight: theme.lineHeights.tight,
  },
  '& .rvh-pill-period': {
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .rvh-pill-skill': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
  },
  '& .rvh-times-wrap': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .rvh-times': {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'nowrap',
    gap: theme.spacing.inputLabelGap,
  },
  '& .rvh-times > *': {
    flexShrink: 0,
  },
  '& .rvh-time-field': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.labelGap,
  },
  '& .rvh-time-label': {
    fontSize: theme.typography.hint.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  '& .rvh-input': {
    padding: theme.spacing.inputPadding,
    fontSize: theme.sizes.smallFontSize,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.input,
    background: theme.colors.bgCard,
    color: theme.colors.textDark,
  },
  '& .rvh-input:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .rvh-input-static': {
    display: 'inline-block',
    color: theme.colors.textMuted,
    background: 'transparent',
    border: 'none',
  },
});

// Translate vertical mouse-wheel events into horizontal scroll on the
// row. Trackpad horizontal gestures already scroll horizontally
// natively; this only kicks in when the user has a regular mouse with
// no horizontal scroll axis. Only redirects vertical deltas when the
// container is actually overflowing, so a wheel on a fully-visible row
// still falls through to the page scroll.
function handleWheelHorizontalScroll(event) {
  const el = event.currentTarget;
  if (el.scrollWidth <= el.clientWidth) return;
  if (event.deltaY === 0) return;
  el.scrollLeft += event.deltaY;
  event.preventDefault();
}

function RouteVehicleHeader({
  vehicle,
  shiftStart,
  shiftEnd,
  targetShiftEnd,
  onShiftStartChange,
  onShiftEndChange,
  onTargetShiftEndChange,
  readOnly,
  validationError,
}) {
  if (!vehicle) return null;

  const period = deriveShiftPeriod(vehicle.shiftStartTime);
  const skillLabel = vehicle.skillSet || '';

  return (
    <StyledDiv
      data-testid="route-vehicle-header"
      onWheel={handleWheelHorizontalScroll}
    >
      <span className="rvh-pill" data-testid="rvh-pill">
        <span className="rvh-pill-period">{period}</span>
        <span className="rvh-pill-skill">{skillLabel}</span>
      </span>
      <span className="rvh-times-wrap">
        <span className="rvh-times">
          <label className="rvh-time-field">
            <span className="rvh-time-label">Shift start</span>
            {readOnly ? (
              <span
                className="rvh-input rvh-input-static"
                data-testid="rvh-shift-start-static"
              >
                {staticTimeText(shiftStart)}
              </span>
            ) : (
              <input
                type="datetime-local"
                className="rvh-input"
                value={toDatetimeLocal(shiftStart)}
                onChange={(e) => onShiftStartChange(e.target.value)}
                data-testid="rvh-shift-start"
              />
            )}
          </label>
          <label className="rvh-time-field">
            <span className="rvh-time-label">Target end</span>
            {readOnly ? (
              <span
                className="rvh-input rvh-input-static"
                data-testid="rvh-target-shift-end-static"
              >
                {staticTimeText(targetShiftEnd)}
              </span>
            ) : (
              <input
                type="datetime-local"
                className="rvh-input"
                value={toDatetimeLocal(targetShiftEnd)}
                onChange={(e) => onTargetShiftEndChange(e.target.value)}
                data-testid="rvh-target-shift-end"
              />
            )}
          </label>
          <label className="rvh-time-field">
            <span className="rvh-time-label">Shift end</span>
            {readOnly ? (
              <span
                className="rvh-input rvh-input-static"
                data-testid="rvh-shift-end-static"
              >
                {staticTimeText(shiftEnd)}
              </span>
            ) : (
              <input
                type="datetime-local"
                className="rvh-input"
                value={toDatetimeLocal(shiftEnd)}
                onChange={(e) => onShiftEndChange(e.target.value)}
                data-testid="rvh-shift-end"
              />
            )}
          </label>
        </span>
        <FieldError
          message={validationError}
          data-testid="rvh-validation-error"
        />
      </span>
    </StyledDiv>
  );
}

RouteVehicleHeader.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.string,
    skillSet: PropTypes.string,
    shiftStartTime: PropTypes.string,
    shiftEndTime: PropTypes.string,
    targetShiftEndTime: PropTypes.string,
  }),
  shiftStart: PropTypes.string.isRequired,
  shiftEnd: PropTypes.string.isRequired,
  targetShiftEnd: PropTypes.string.isRequired,
  onShiftStartChange: PropTypes.func.isRequired,
  onShiftEndChange: PropTypes.func.isRequired,
  onTargetShiftEndChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  validationError: PropTypes.string,
};

RouteVehicleHeader.defaultProps = {
  vehicle: null,
  readOnly: false,
  validationError: '',
};

export default RouteVehicleHeader;
